import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { toast } from "sonner";
import { Bot, GraduationCap, Lightbulb, Plus, Trash2, X } from "lucide-react";
import {
  getPlatformAssistantSettings,
  updatePlatformAssistantSettings,
  getPlatformAssistantStats,
  getPlatformUnanswered,
  platformTeach,
  platformDismiss,
} from "../api/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import AssistantStats from "./AssistantStats";
import TeachAnswerDialog from "./TeachAnswerDialog";

// Platform Admin → one clinic's lead assistant: which engine answers, how it
// is really doing, what it should learn (Aivexa approves every answer), what
// it has learned, and the clinic's own words. Everything goes through the
// clinic's own API via the Platform Admin service account.

const ENGINES = [
  { key: "script", label: "Smart assistant (free)", hint: "Rules + live clinic data + answers taught here. No AI cost." },
  { key: "ollama", label: "Smart assistant + local AI", hint: "Adds a private AI model on our own server for questions the rules don't cover." },
  { key: "anthropic", label: "Claude AI", hint: "Full AI conversation. Paid per message; needs the API key on the clinic's server." },
  { key: "auto", label: "Auto", hint: "Best engine the clinic's server is configured for." },
];

const Section = ({ title, icon: Icon, description, children, action }) => (
  <div className="space-y-3 border-t border-border pt-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />} {title}
        </p>
        {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export default function PlatformAssistant({ clinic }) {
  const key = clinic.key;
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState(null);
  const [teaching, setTeaching] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [word, setWord] = useState({ word: "", means: "" });

  const load = useCallback(() => {
    setError(null);
    getPlatformAssistantSettings(key)
      .then((res) => setSettings(res.data))
      .catch((err) => setError(err?.message || "Could not reach this clinic's assistant."));
    getPlatformUnanswered(key)
      .then((res) => setGroups(res.data))
      .catch(() => setGroups([]));
    setReloadKey((k) => k + 1);
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, success) => {
    setSaving(true);
    try {
      await updatePlatformAssistantSettings(key, patch);
      toast.success(success);
      load();
    } catch (err) {
      toast.error(err?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async (g) => {
    try {
      await platformDismiss(key, g.message_ids);
      setGroups((all) => all.filter((x) => x !== g));
    } catch (err) {
      toast.error(err?.message || "Could not dismiss.");
    }
  };

  const approve = async (g) => {
    const [first, ...rest] = g.questions;
    try {
      await platformTeach(key, { question: first, extra_questions: rest, answer: g.suggested_answer, topic: "", kb_index: null, message_ids: g.message_ids });
      toast.success("Approved — the assistant answers this from now on.");
      load();
    } catch (err) {
      toast.error(err?.message || "Could not approve.");
    }
  };

  if (error) {
    return (
      <Section title="Lead assistant" icon={Bot}>
        <p className="rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">{error}</p>
      </Section>
    );
  }
  if (!settings) {
    return (
      <Section title="Lead assistant" icon={Bot}>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </Section>
    );
  }

  const kb = settings.knowledge_base || [];
  const learned = kb.map((k, i) => ({ ...k, index: i })).filter((k) => k.examples?.length);
  const suggestions = (groups || []).filter((g) => g.suggested);
  const unanswered = (groups || []).filter((g) => !g.suggested);
  const vocabulary = settings.vocabulary || [];

  // Only the fields the clinic's API accepts — entries arrive with ids.
  const clean = ({ topic, answer, examples, taught_by_name, taught_at }) => ({ topic, answer, examples: examples || [], taught_by_name, taught_at });
  const removeExample = (index, j) =>
    save(
      { knowledge_base: kb.map((k, i) => clean(i === index ? { ...k, examples: k.examples.filter((_, x) => x !== j) } : k)) },
      "Removed — the assistant won't use that wording any more."
    );
  const removeTopic = (index) =>
    save({ knowledge_base: kb.filter((_, i) => i !== index).map(clean) }, "Answer removed.");

  const addWord = (e) => {
    e.preventDefault();
    const w = word.word.trim().toLowerCase();
    const m = word.means.trim().toLowerCase();
    if (!w || !m) return toast.error("Both the word and its meaning are needed.");
    save({ vocabulary: [...vocabulary.filter((v) => v.word !== w), { word: w, means: m }] }, `“${w}” now means “${m}”.`);
    setWord({ word: "", means: "" });
  };

  return (
    <>
      <Section title="Lead assistant" icon={Bot} description="What answers this clinic's patients, and how it's really doing.">
        <div className="grid gap-2 sm:grid-cols-2">
          {ENGINES.map((e) => (
            <label
              key={e.key}
              className={`flex cursor-pointer gap-2 rounded-md border p-3 text-sm ${settings.engine_choice === e.key ? "border-primary bg-primary/5" : "border-border"}`}
            >
              <input
                type="radio"
                name={`engine-${key}`}
                checked={settings.engine_choice === e.key}
                onChange={() => save({ engine: e.key }, `${clinic.name}: ${e.label}.`)}
                disabled={saving}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">{e.label}</span>
                <span className="block text-xs text-muted-foreground">{e.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant={settings.engine_ready ? "secondary" : "warning"}>
            Answering now: {ENGINES.find((e) => e.key === settings.engine)?.label ?? settings.engine}
            {settings.engine_model ? ` · ${settings.engine_model}` : ""}
            {settings.engine_ready ? "" : " — not ready, unclear questions go to staff"}
          </Badge>
          <Badge variant={settings.enabled ? "secondary" : "warning"}>{settings.enabled ? "On" : "Switched off by the clinic"}</Badge>
          <Badge variant={settings.whatsapp_configured ? "secondary" : "outline"}>{settings.whatsapp_configured ? "WhatsApp connected" : "WhatsApp not connected"}</Badge>
        </div>
        <AssistantStats load={() => getPlatformAssistantStats(key).then((res) => res.data)} reloadKey={reloadKey} />
      </Section>

      <Section
        title={`Suggested answers${suggestions.length ? ` (${suggestions.length})` : ""}`}
        icon={Lightbulb}
        description="Questions patients asked that the assistant didn't understand, where staff replied the same way at least twice. Approving teaches every wording. Replies meant for one patient are never suggested."
      >
        {suggestions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No suggestions yet — they appear once staff have answered the same question the same way twice.</p>
        ) : (
          suggestions.map((g) => (
            <div key={g.message_ids[0]} className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
              <p className="text-sm">
                <span className="font-medium">Patients asked</span> ({g.count}×): {g.questions.map((q) => `“${q}”`).join(", ")}
              </p>
              <p className="rounded bg-card px-2.5 py-2 text-sm">
                <span className="text-xs font-medium text-muted-foreground">Staff answered ({g.agreeing_replies}× alike): </span>
                {g.suggested_answer}
              </p>
              <div className="flex flex-wrap justify-end gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => dismiss(g)}>Dismiss</Button>
                <Button size="sm" variant="outline" onClick={() => setTeaching({ ...g, answer: g.suggested_answer })}>Edit first</Button>
                <Button size="sm" onClick={() => approve(g)}><GraduationCap className="h-3.5 w-3.5" /> Approve</Button>
              </div>
            </div>
          ))
        )}
      </Section>

      <Section
        title={`Couldn't answer${unanswered.length ? ` (${unanswered.length})` : ""}`}
        icon={GraduationCap}
        description="Other questions from the last 60 days the assistant didn't understand. Teach the answer once and it handles them from then on."
      >
        {unanswered.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing waiting.</p>
        ) : (
          <div className="divide-y divide-border rounded-md border border-border">
            {unanswered.slice(0, 30).map((g) => (
              <div key={g.message_ids[0]} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{g.questions.map((q) => `“${q}”`).join(", ")}</p>
                  <p className="text-xs text-muted-foreground">
                    {g.count}× · last {moment(g.last_at).fromNow()}
                    {g.staff_reply ? ` · staff: “${g.staff_reply.slice(0, 70)}${g.staff_reply.length > 70 ? "…" : ""}”` : " · no staff reply yet"}
                    {g.personal_replies ? ` · ${g.personal_replies} personal repl${g.personal_replies > 1 ? "ies" : "y"} not suggested` : ""}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" onClick={() => dismiss(g)}>Dismiss</Button>
                  <Button size="sm" variant="outline" onClick={() => setTeaching({ ...g, answer: g.staff_reply })}>Teach</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title={`Learned answers (${learned.length})`} icon={GraduationCap} description="Everything the assistant learned from real chats — who approved it and when. Remove anything wrong or outdated.">
        {learned.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing learned yet.</p>
        ) : (
          learned.map((k) => (
            <div key={k.index} className="rounded-md border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{k.topic}</p>
                  <p className="text-sm text-muted-foreground">{k.answer}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {k.taught_by_name ? `Approved by ${k.taught_by_name}` : "Approved"}
                    {k.taught_at ? ` · ${moment(k.taught_at).format("D MMM YYYY")}` : ""}
                  </p>
                </div>
                <button onClick={() => removeTopic(k.index)} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${k.topic}`} disabled={saving}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {k.examples.map((ex, j) => (
                  <Badge key={j} variant="outline" className="gap-1 font-normal">
                    {ex}
                    <button onClick={() => removeExample(k.index, j)} className="text-muted-foreground hover:text-destructive" aria-label={`Remove “${ex}”`} disabled={saving}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          ))
        )}
      </Section>

      <Section title={`Clinic vocabulary (${vocabulary.length})`} description="Words this clinic's patients use, and what they mean to the assistant — e.g. “chamri” → “skin”, “somvaar” → “monday”.">
        <div className="flex flex-wrap gap-1.5">
          {vocabulary.map((v) => (
            <Badge key={v.word} variant="outline" className="gap-1 font-normal">
              {v.word} → {v.means}
              <button
                onClick={() => save({ vocabulary: vocabulary.filter((x) => x.word !== v.word) }, `“${v.word}” removed.`)}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${v.word}`}
                disabled={saving}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {vocabulary.length === 0 && <span className="text-sm text-muted-foreground">None yet.</span>}
        </div>
        <form onSubmit={addWord} className="flex flex-wrap gap-2">
          <Input value={word.word} onChange={(e) => setWord((w) => ({ ...w, word: e.target.value }))} placeholder="Patient's word, e.g. chamri" className="h-9 w-48" />
          <Input value={word.means} onChange={(e) => setWord((w) => ({ ...w, means: e.target.value }))} placeholder="Means, e.g. skin" className="h-9 w-48" />
          <Button type="submit" size="sm" variant="outline" disabled={saving}><Plus className="h-3.5 w-3.5" /> Add word</Button>
        </form>
      </Section>

      <TeachAnswerDialog
        open={!!teaching}
        onOpenChange={(open) => !open && setTeaching(null)}
        question={teaching?.questions?.[0]}
        extraQuestions={teaching?.questions || []}
        answer={teaching?.answer}
        messageIds={teaching?.message_ids}
        loadTopics={() => Promise.resolve(kb)}
        teach={(payload) => platformTeach(key, payload)}
        onDone={load}
      />
    </>
  );
}
